import {
  contactsCreateMutation,
  contactsListQueryKey,
  contactsPartialUpdateMutation,
  contactsRetrieveOptions,
  contactsRetrieveQueryKey,
  customFieldsListOptions,
  Dropdown,
  EntityTypeEnum,
  LeadsListListData,
  leadsListListInfiniteOptions,
  opportunitiesListQueryKey,
  opportunitiesRetrieveQueryKey,
  PaginatedDropdownList,
  Type83fEnum
} from '@crm/api'
import {
  CustomField,
  DuplicateAlert,
  InfiniteSelect,
  PhoneInput
} from '@crm/components'
import {
  defaultNonPagination,
  handleContactMutationError,
  transformCustomFieldValues,
  transformCustomFieldValuesToForm
} from '@crm/utils'
import { Text } from '@shared/components'
import { useInvalidatePredicateQueries, useSessionContext } from '@shared/hooks'
import { useLocales } from '@shared/locales'

import { useMutation, useQuery } from '@tanstack/react-query'
import {
  App,
  Button,
  Col,
  Flex,
  Form,
  FormInstance,
  Input,
  Modal,
  Row,
  Switch,
  Tooltip
} from 'antd'
import { Add, Trash } from 'iconsax-react'
import { useEffect } from 'react'

import type { AnyObject } from 'antd/es/_util/type'
import type { ModalProps } from 'antd/lib'

type Props = {
  form: FormInstance
  id?: string | number
  opportunityId?: number
  onClose: () => void
  initialValues?: AnyObject
} & ModalProps

export const ContactForm = ({
  form,
  id,
  opportunityId,
  onClose,
  initialValues,
  ...props
}: Props) => {
  const { t } = useLocales()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { user } = useSessionContext()
  const { message } = App.useApp()

  const phoneNumbers = Form.useWatch('phone_numbers', form)

  const { data: customFields } = useQuery({
    ...customFieldsListOptions({
      query: {
        entity_type: [EntityTypeEnum.CONTACT],
        ...defaultNonPagination
      }
    })
  })

  const { data } = useQuery({
    ...contactsRetrieveOptions({
      path: { id: Number(id) }
    }),
    enabled: !!id
  })

  useEffect(() => {
    if (data) {
      const customFieldFormData = transformCustomFieldValuesToForm(
        data?.custom_field_values || []
      )

      form.setFieldsValue({
        ...data,
        custom_field_values: customFieldFormData || undefined,
        lead: data?.lead
      })
    }
  }, [data, form, user?.user, customFields])

  const onSuccessMutation = () => {
    invalidateQueries([
      contactsListQueryKey(),
      opportunitiesListQueryKey(),
      opportunitiesRetrieveQueryKey({ path: { id: Number(opportunityId) } }),
      contactsRetrieveQueryKey({
        path: { id: Number(id) }
      })
    ])
    onClose()
    form.resetFields()

    message.success(t('success.success_data_save'))
  }

  const onErrorMutation = (error: unknown) =>
    handleContactMutationError(error, form)

  const {
    mutate: createMutate,
    isPending: isCreatePending,
    error: createError,
    reset: resetCreate
  } = useMutation({
    ...contactsCreateMutation(),
    onSuccess: onSuccessMutation,
    onError: onErrorMutation
  })

  const {
    mutate: editMutate,
    isPending: isEditPending,
    error: editError,
    reset: resetEdit
  } = useMutation({
    ...contactsPartialUpdateMutation(),
    onSuccess: onSuccessMutation,
    onError: onErrorMutation
  })

  // Clear the duplicate alert once the user edits any phone number
  useEffect(() => {
    resetCreate()
    resetEdit()
  }, [phoneNumbers, resetCreate, resetEdit])

  const onChangePrimaryPhone = (checked: boolean, currentIndex: number) => {
    if (checked) {
      const phoneNumbers = form.getFieldValue('phone_numbers') || []

      phoneNumbers?.forEach((_: unknown, index: number) => {
        if (index !== currentIndex) {
          form.setFieldValue(['phone_numbers', index, 'is_primary'], false)
        }
      })
    }
  }

  const onSubmit = async () => {
    await form.validateFields()
    const values = form.getFieldsValue(true)

    const transformedCustomFields = customFields?.results
      ? transformCustomFieldValues(
          values?.custom_field_values,
          customFields.results
        )
      : []

    if (id) {
      editMutate({
        path: { id: Number(id) },
        body: { ...values, custom_field_values: transformedCustomFields }
      })
    } else {
      createMutate({
        body: { ...values, custom_field_values: transformedCustomFields }
      })
    }
  }

  const availableCustomFields = customFields?.results?.filter(
    (e) => !e.deleted_at
  )

  const { filteredCustomFields, checkboxCustomFields } = Object.groupBy(
    availableCustomFields || [],
    (e) =>
      e.type === Type83fEnum.BOOLEAN
        ? 'checkboxCustomFields'
        : 'filteredCustomFields'
  )
  const formInitialValues = {
    phone_numbers: [{ is_primary: true }],
    ...initialValues
  }

  const isPending = isCreatePending || isEditPending

  return (
    <Modal
      width={800}
      maskClosable={false}
      title={id ? t('contacts.edit_contact') : t('contacts.add_contact')}
      onCancel={onClose}
      destroyOnHidden
      okButtonProps={{
        htmlType: 'submit',
        loading: isPending,
        onClick: onSubmit
      }}
      cancelText={t('buttons.cancel')}
      okText={t('buttons.save')}
      {...props}
    >
      <Form form={form} layout="vertical" initialValues={formInitialValues}>
        <DuplicateAlert
          error={createError ?? editError}
          onClose={() => {
            resetCreate()
            resetEdit()
          }}
        />

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="lead"
              label={t('lead.lead')}
              rules={[{ required: true }]}
            >
              <InfiniteSelect<
                LeadsListListData,
                PaginatedDropdownList,
                Dropdown
              >
                className="w-full"
                popupMatchSelectWidth
                placeholder={t('lead.leads')}
                getQueryFunction={leadsListListInfiniteOptions}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="email"
              label={t('contacts.email')}
              rules={[{ required: true, type: 'email' }]}
            >
              <Input placeholder={t('contacts.email')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="first_name"
              label={t('contacts.first_name')}
              rules={[{ required: true }]}
            >
              <Input placeholder={t('contacts.first_name')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="last_name"
              label={t('contacts.last_name')}
              rules={[{ required: true }]}
            >
              <Input placeholder={t('contacts.last_name')} />
            </Form.Item>
          </Col>
        </Row>
        <Col span={24}>
          <Form.List name="phone_numbers">
            {(fields, { add, remove }, { errors }) => (
              <Row gutter={[12, 12]} align="middle" justify="space-between">
                <Col span={12}>
                  <Text>{t('contacts.phone_numbers')}</Text>
                </Col>

                <Col span={12}>
                  <Flex justify="end">
                    <Button
                      variant="dashed"
                      color="primary"
                      onClick={() => add()}
                      icon={<Add />}
                    >
                      {t('contacts.add_phone_number')}
                    </Button>
                  </Flex>
                </Col>

                {fields?.map(({ key, name, ...restField }) => (
                  <Col span={12} key={key}>
                    <Flex key={key} gap={8} align="center">
                      <Form.Item
                        {...restField}
                        name={[name, 'number']}
                        rules={[
                          {
                            required: true,
                            message: t(
                              'account_settings.this_field_is_required'
                            )
                          }
                        ]}
                      >
                        <PhoneInput placeholder={t('contacts.phone_number')} />
                      </Form.Item>

                      <Tooltip title={t('contacts.set_primary_number')}>
                        <Form.Item
                          {...restField}
                          name={[name, 'is_primary']}
                          valuePropName="checked"
                        >
                          <Switch
                            onChange={(checked) =>
                              onChangePrimaryPhone(checked, name)
                            }
                          />
                        </Form.Item>
                      </Tooltip>

                      <Form.Item>
                        <Button
                          danger
                          icon={<Trash size={16} />}
                          onClick={() => remove(name)}
                        />
                      </Form.Item>
                    </Flex>
                  </Col>
                ))}

                <Col span={24}>
                  <Text color="colorError">{errors?.join?.(', ')}</Text>
                </Col>
              </Row>
            )}
          </Form.List>
        </Col>

        <Row gutter={12} className="mb-10">
          <Col span={24}>
            <Text size={16} weight={500}>
              {t('nomenclature.custom_fields')}
            </Text>
          </Col>
        </Row>

        <Row gutter={12}>
          {filteredCustomFields?.map((item) => (
            <Col span={12} key={item.id}>
              <CustomField {...item} />
            </Col>
          ))}

          {checkboxCustomFields?.map((item) => (
            <Col span={24} key={item.id}>
              <CustomField {...item} />
            </Col>
          ))}
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item
              name="additional_data"
              label={t('contacts.additional_data')}
            >
              <Input.TextArea
                rows={4}
                placeholder={t('contacts.additional_data')}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
